<script setup>
import {
  ref,
  reactive,
  watch,
  computed,
  onMounted,
} from 'vue';
import { useToast } from 'vue-toastification';
import { isIOS } from '@/utils/browser';
import dayjs from 'dayjs';
import FormInput from '@/components/form/FormInput.vue';
import FormCheckBox from '@/components/form/FormCheckBox.vue';
import FormRadio from '@/components/form/FormRadio.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import FormTextArea from '@/components/form/FormTextArea.vue';
import FordButton from '@/components/FordButton.vue';
import statusCodes from './statusCodes';

const emit = defineEmits(['submit']);
const props = defineProps({
  solutions: {
    type: Array,
    default: () => [],
  },
  appointmentOptions: {
    type: Array,
    default: () => [],
  },
  prepopulate: Object, // { comment , user: { userprepopulate }}
  subText: String,
  statuses: Object,
  timeZone: String,
  serverErrorMessage: String,
});

const chosenSolution = ref();

const textInputs = reactive({
  eventDay: '',
  firstName: '',
  lastName: '',
  company: '',
  phone: '',
  comments: '',
  zip: '',
});

const allowContactInterested = reactive({
  checked: true,
  text: `I would like Ford Motor Company,
    its affiliates, authorized dealers, and
    business partners to contact me about the
    information I've indicated I'm interested in.`,
});

const allowContactOutside = reactive({
  checked: true,
  text: `I agree that Ford Motor Company, its affiliates, authorized dealers,
    and business partners can contact me about products, services,
    news, offers, and incentives. I understand that unsubscribing
    from communications from Ford Motor Company will not affect communications
    I receive from others, who may provide their own methods for unsubscribing.`,
});

const mapSessionPrepropulate = () => {
  const session = props.prepopulate;

  textInputs.firstName = session.user.firstName;
  textInputs.lastName = session.user.lastName;
  textInputs.company = session.user.companyName;
  textInputs.phone = session.user.phone;
  textInputs.zip = session.user.zip;
  textInputs.comments = session.comment;

  allowContactOutside.value = session.user?.userProfileQuestion.allowContactInterested;
  allowContactInterested.value = session.user?.userProfileQuestion.allowContactInterested;

  chosenSolution.value = session.user?.userProfileQuestion?.interestedInFordProSolutions;
};

const eventDays = computed(
  () => props.appointmentOptions.map((x) => ({ text: x.date, value: x.date }))
    .sort((a, b) => (
        a.value === b.value ? 0 // eslint-disable-line
        : a.value > b.value ? 1
          : -1
    )),
);

const formatTime = (time) => `${dayjs(`0000-00-00T${time}`).format('h:mm A')} ${props.timeZone}`;

const eventTimes = computed(
  () => props.appointmentOptions.find(
    (x) => x.date === textInputs.eventDay,
  )?.times
    .sort((a, b) => (
      a.value === b.value ? 0 // eslint-disable-line
        : a.value > b.value ? 1
          : -1
    ))
    .map((x) => ({ text: formatTime(x.value), value: x.id })),
);

const toast = useToast();

const validate = () => {
  const {
    firstName,
    lastName,
    company,
    phone,
    eventTime,
    zip,
  } = textInputs;

  if (!eventTime) {
    return { message: 'Please select an event time', valid: false };
  }

  if (!firstName || !lastName || !company || !zip) {
    return { message: 'Please provide values for all required fields', valid: false };
  }

  if ((phone.length > 0) && (phone.replace(/[^0-9.]/g, '').length < 10)) {
    return { message: 'Please provide your full phone number including area code', valid: false };
  }

  if (!chosenSolution.value) {
    return { message: 'Please provide values for all required fields', valid: false };
  }

  return { valid: true };
};

const mapPayload = () => {
  const {
    eventTime,
    firstName,
    lastName,
    company,
    phone,
    comments,
    zip,
  } = textInputs;

  return {
    appointmentId: eventTime,
    comment: comments,
    user: {
      firstName,
      lastName,
      phone,
      companyName: company,
      zip,
      questions: {
        interestedInFordProSolutions: chosenSolution.value,
        allowContactInterested: allowContactInterested.checked,
        allowContactOutside: allowContactOutside.checked,
      },
    },
  };
};

const loading = ref(false);
const logo = ref(null);

const submit = () => {
  toast.clear();
  const validation = validate();

  if (!validation.valid && isIOS()) { // accepted workaround for ios toast bug: scroll to the top
    document.activeElement.blur();
    logo.value.$el.scrollTo(0, 0);
    setTimeout(() => {
      toast.error(validation.message, { timeout: 10000 });
    }, 100);
  } else if (!validation.valid) {
    toast.error(validation.message, { timeout: 10000 });
  } else {
    loading.value = true;
    emit('submit', mapPayload());
  }
};

// show error validation toast
watch(
  () => props.statuses.scheduleStatus === statusCodes.failed,
  (obj) => {
    if (obj) {
      toast.error(
        props.serverErrorMessage
        || 'We could not complete your registration for this appointment. Please try again later.',
        { timeout: 10000 },
      );
      loading.value = false;
    }
  },
  { deep: true },
);

watch(() => props.prepopulate, () => mapSessionPrepropulate());

onMounted(() => {
  if (props.prepopulate) {
    mapSessionPrepropulate();
  }
});
</script>

<template>
  <div
    @keydown.enter="submit"
    class="
      background-wrap
      bg-ford-gray-light
      px-[50px]
      py-[50px]
      tablet-sm:px-[100px]
      tablet-sm:py-[50px]
    "
  >
    <div class="form-wrap max-w-[700px] mx-auto">
      <h1 class="font-FordAntennaSemiBold text-[24px] mb-[25px] tablet:text-center">
        Need some one-on-one time with a Ford Pro™ team member?
      </h1>
      <p class="
        text-ford-gray-darker
        font-FordAntennaRegular
        text-[16px]
        mb-[35px]
        tablet:text-center
      ">
        {{ subText }}
      </p>
      <div
        class="
          grid
          grid-cols-1 desktop:grid-cols-2
          desktop:gap-[2rem]
        ">
        <form-select
          ref="event-day"
          v-model="textInputs.eventDay"
          placeholder="Select Day *"
          aria-label="Select Event Day"
          :data="eventDays"
          required
        />
        <form-select
          :disabled="!eventTimes"
          ref="event-time"
          v-model="textInputs.eventTime"
          placeholder="Select Time *"
          aria-label="Select Event Time"
          :data="eventTimes"
          required
        />
      </div>
      <hr class="
        mx-auto
        max-w-[50rem]
        mb-[2.5rem] hidden tablet:block
      " />
      <p
        class="
          mb-[1rem] tablet:mb-[2rem]
          tablet:text-center
        ">
        Discover how Ford Pro™ can be a productivity accelerator for your business.
      </p>
      <form-input
        label="First Name"
        placeholder="First Name"
        v-model="textInputs.firstName"
        required
      />
      <form-input
        label="Last Name"
        placeholder="Last Name"
        v-model="textInputs.lastName"
        required
      />
      <div
        class="
          grid
          grid-cols-1 desktop:grid-cols-2
          desktop:gap-[2rem]
        "
      >
        <form-input
          label="Company"
          placeholder="Company"
          v-model="textInputs.company"
          required
        />
        <form-input
          label="Mobile Phone"
          v-model="textInputs.phone"
          name="phone"
          title="Please provide your full phone number including area code."
          placeholder="(000) 000-0000"
          type="tel"
        />
      </div>

      <form-input
          label="Zip Code"
          v-model="textInputs.zip"
          maxlength="10"
          name="zip"
          title="Please provide your zip code."
          placeholder="12345"
          type="zip"
          required
        />
      <form-text-area
        rows="5"
        label="Comments"
        v-model="textInputs.comments"
      />

      <h2 class="text-black font-fordAntennaMedium mb-[22px]">
        Select the solution group you would like to meet with:
        <sup class="text-red-500">*</sup>
      </h2>

      <p class="mb-6 text-[13px] text-ford-blue-dark">
          To better serve you, we are limiting one
          Ford Pro™ Solution Expert per scheduled appointment.
          To meet with additional solution groups one-on-one, please book separate appointments.
          Visit the Ford Pro™ booth to interact with the team and see product demonstrations,
          even without an appointment.
      </p>

      <div class="solutions flex flex-wrap [&>*]:min-w-[25%] mb-[35px]">
        <form-radio
          v-for="box in solutions"
          v-model="chosenSolution"
          name="picksolution"
          :value="box.name"
          :label="box.name"
          :key="box.id"
        />
      </div>

      <form-check-box
        class="contact-me-ford"
        v-model="allowContactInterested.checked"
        :label="allowContactInterested.text"
      />

      <form-check-box
        class="contact-me-affiliates mb-[35px]"
        v-model="allowContactOutside.checked"
        :label="allowContactOutside.text"
      />
      <div class="flex">
        <ford-button class="mt-[35px] tablet:mx-auto" @method="submit">
          <img v-if="loading"
            :src="require('@/assets/img/spinner.svg')" alt="spinner"
          />
          <span v-else>Schedule</span>
        </ford-button>
      </div>
    </div> <!-- .form-wrap -->
  </div> <!-- .background-wrap -->
</template>
